import { useState, useEffect } from 'react';

interface CompromissoData {
  nome: string;
  periodo: number;
  dataInicio: string;
  diasPraticados: string[];
  assinado: boolean;
}

export default function CompromissoPratica() {
  const [compromisso, setCompromisso] = useState<CompromissoData>({
    nome: '',
    periodo: 21,
    dataInicio: new Date().toISOString().split('T')[0],
    diasPraticados: [],
    assinado: false
  });

  useEffect(() => {
    const saved = localStorage.getItem('compromisso-reframing');
    if (saved) {
      try {
        setCompromisso(JSON.parse(saved));
      } catch (e) {
        console.error('Erro ao carregar compromisso');
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('compromisso-reframing', JSON.stringify(compromisso));
  }, [compromisso]);

  const assinarCompromisso = () => {
    if (compromisso.nome.trim()) {
      setCompromisso({ ...compromisso, assinado: true, dataInicio: new Date().toISOString().split('T')[0] });
    }
  };

  const toggleDia = (dia: string) => {
    const diasPraticados = compromisso.diasPraticados.includes(dia)
      ? compromisso.diasPraticados.filter(d => d !== dia)
      : [...compromisso.diasPraticados, dia];
    setCompromisso({ ...compromisso, diasPraticados });
  };

  const gerarCalendario = () => {
    const dias = [];
    const dataInicio = new Date(compromisso.dataInicio);
    for (let i = 0; i < compromisso.periodo; i++) {
      const data = new Date(dataInicio);
      data.setDate(dataInicio.getDate() + i);
      dias.push(data.toISOString().split('T')[0]);
    }
    return dias;
  };

  const calcularEstatisticas = () => {
    const total = compromisso.periodo;
    const praticados = compromisso.diasPraticados.length;
    const progresso = Math.round((praticados / total) * 100);
    
    let maiorSequencia = 0;
    let sequenciaAtual = 0;
    const diasOrdenados = [...compromisso.diasPraticados].sort();
    
    diasOrdenados.forEach((dia, index) => {
      if (index === 0) {
        sequenciaAtual = 1;
      } else {
        const diaAnterior = new Date(diasOrdenados[index - 1]);
        const diaAtual = new Date(dia);
        const diff = Math.floor((diaAtual.getTime() - diaAnterior.getTime()) / (1000 * 60 * 60 * 24));
        
        if (diff === 1) {
          sequenciaAtual++;
        } else {
          maiorSequencia = Math.max(maiorSequencia, sequenciaAtual);
          sequenciaAtual = 1;
        }
      }
    });
    maiorSequencia = Math.max(maiorSequencia, sequenciaAtual);

    return { total, praticados, progresso, maiorSequencia };
  };

  const stats = calcularEstatisticas();

  if (!compromisso.assinado) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Compromisso de Prática
            </h2>
            <p className="text-lg text-gray-600">
              Assine seu compromisso pessoal de praticar o Reframing diariamente
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-3xl shadow-lg">
            <div className="bg-white p-8 rounded-2xl mb-6">
              <p className="text-gray-700 leading-relaxed mb-6 text-center italic">
                "Eu, <strong>{compromisso.nome || '_______________'}</strong>, comprometo-me a praticar a técnica de Ressignificação 
                por <strong>{compromisso.periodo} dias consecutivos</strong>, transformando situações limitantes em perspectivas 
                empoderadoras e expandindo minha liberdade emocional."
              </p>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Seu Nome Completo
                  </label>
                  <input
                    type="text"
                    value={compromisso.nome}
                    onChange={(e) => setCompromisso({ ...compromisso, nome: e.target.value })}
                    placeholder="Digite seu nome completo"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Período de Compromisso
                  </label>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {[7, 14, 21, 30, 60, 90].map((dias) => (
                      <button
                        key={dias}
                        onClick={() => setCompromisso({ ...compromisso, periodo: dias })}
                        className={`py-3 px-4 rounded-xl font-semibold transition-all whitespace-nowrap ${
                          compromisso.periodo === dias
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg scale-105'
                            : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        {dias} dias
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={assinarCompromisso}
              disabled={!compromisso.nome.trim()}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-quill-pen-line text-xl"></i>
              Assinar Compromisso
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Seu Compromisso de Prática
          </h2>
          <p className="text-lg text-gray-600">
            Acompanhe seu progresso e marque os dias praticados
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-purple-500 to-pink-500 text-white p-6 rounded-2xl shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <i className="ri-calendar-check-line text-3xl"></i>
              <span className="text-4xl font-bold">{stats.praticados}</span>
            </div>
            <p className="text-white/90 font-medium">Dias Praticados</p>
          </div>

          <div className="bg-gradient-to-br from-pink-500 to-rose-500 text-white p-6 rounded-2xl shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <i className="ri-fire-line text-3xl"></i>
              <span className="text-4xl font-bold">{stats.maiorSequencia}</span>
            </div>
            <p className="text-white/90 font-medium">Maior Sequência</p>
          </div>

          <div className="bg-gradient-to-br from-rose-500 to-orange-500 text-white p-6 rounded-2xl shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <i className="ri-trophy-line text-3xl"></i>
              <span className="text-4xl font-bold">{stats.progresso}%</span>
            </div>
            <p className="text-white/90 font-medium">Progresso</p>
          </div>
        </div>

        {stats.maiorSequencia >= 7 && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6 rounded-2xl mb-8 shadow-lg">
            <div className="flex items-center gap-4">
              <i className="ri-fire-fill text-4xl"></i>
              <div>
                <h4 className="font-bold text-lg">🔥 Sequência de {stats.maiorSequencia} dias!</h4>
                <p className="text-white/90">Você está criando um hábito poderoso. Continue assim!</p>
              </div>
            </div>
          </div>
        )}

        {stats.progresso === 100 && (
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8 rounded-2xl mb-8 shadow-xl text-center">
            <i className="ri-trophy-fill text-6xl mb-4 inline-block"></i>
            <h4 className="font-bold text-2xl mb-2">🎉 Parabéns, {compromisso.nome}!</h4>
            <p className="text-white/90 text-lg">
              Você completou seu compromisso de {compromisso.periodo} dias! Você transformou o Reframing em um hábito poderoso.
            </p>
          </div>
        )}

        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-3xl shadow-lg">
          <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
            Calendário de Prática - {compromisso.periodo} Dias
          </h3>

          <div className="grid grid-cols-7 gap-3">
            {gerarCalendario().map((dia, index) => {
              const praticado = compromisso.diasPraticados.includes(dia);
              const data = new Date(dia);
              const hoje = new Date().toISOString().split('T')[0];
              const ehHoje = dia === hoje;
              const passou = dia < hoje;

              return (
                <button
                  key={dia}
                  onClick={() => toggleDia(dia)}
                  className={`aspect-square rounded-xl font-semibold transition-all duration-300 flex flex-col items-center justify-center gap-1 ${
                    praticado
                      ? 'bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-lg scale-105'
                      : ehHoje
                      ? 'bg-amber-100 text-amber-700 border-2 border-amber-400 hover:bg-amber-200'
                      : passou
                      ? 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                  }`}
                >
                  <span className="text-xs opacity-75">Dia</span>
                  <span className="text-lg">{index + 1}</span>
                  {praticado && <i className="ri-check-line text-sm"></i>}
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex items-center justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gradient-to-br from-purple-600 to-pink-600 rounded"></div>
              <span className="text-gray-600">Praticado</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-amber-100 border-2 border-amber-400 rounded"></div>
              <span className="text-gray-600">Hoje</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-white border-2 border-gray-200 rounded"></div>
              <span className="text-gray-600">Pendente</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}